import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorHandling } from 'src/api/apierrorhandling';
import { postNewRecord, putEditRecord } from 'src/api/stoApi';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { UploadIcon } from 'src/components/ui/UploadIcon';
import { Colors } from 'src/components/utils/Theme';
import { Attachment, IRecordGetResponse, IRecordPostPayload, IRecordPutPayload } from 'src/models';
import { TReportingDocumentType } from 'src/models/enum';
import { array, object, string } from 'yup';
import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { DefaultTextAreaField } from 'src/components/mui-overrides/Textarea';
import { NomenclatureLabel } from '../../ui/NomenclatureLabel';

interface Props {
  record: IRecordGetResponse | null;
  open: boolean;
  assetId: string;
  handleClose: () => void;
  reload: () => void;
}

export const AddEditRecordsDialog: FC<Props> = ({ open, record, assetId, handleClose, reload }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const validationSchema = object({
    title: string().required(t('dashboard.assets.records.addrecords.title.required')),
    description: string().required(t('dashboard.assets.records.addrecords.description.required')),
    documents: array(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      title: record?.title ?? '',
      description: record?.description ?? '',
      documents: record?.documents ?? [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log(values);
      setIsLoading(true);
      try {
        if (record) {
          // update
          const body: IRecordPutPayload = {
            description: values.description,
            documents: values.documents,
            title: values.title,
          };
          await putEditRecord(record.id, body);
        } else {
          // add
          const body: IRecordPostPayload = {
            assetId: assetId,
            description: values.description,
            documents: values.documents,
            title: values.title,
          };
          await postNewRecord(body);
        }
        reload();
        handleCloseAndReset();
      } catch (error) {
        apiErrorHandling(error, t('dashboard.assets.records.addrecords.error'));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleCloseAndReset = () => {
    handleClose();
    formik.resetForm();
  };

  const deleteFileClicked = (index: number) => {
    const newDocuments = [...formik.values.documents];
    newDocuments.splice(index, 1);
    formik.setFieldValue('documents', newDocuments);
  };

  return (
    <Dialog open={open} onClose={handleCloseAndReset} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {record ? t('dashboard.assets.records.addrecords.updateheadline') : t('dashboard.assets.records.addrecords.addheadline')}
        </DialogTitle>

        <DialogContent>
          <DefaultTextField label={t('dashboard.assets.records.addrecords.title.label')} fieldName={'title'} formik={formik} />
          <DefaultTextAreaField
            fieldName="description"
            label={t('dashboard.assets.records.addrecords.description.label')}
            formik={formik}
          />
          <UploadIcon
            newFile={(attachment: Attachment) => {
              formik.setFieldValue('documents', [
                ...formik.values.documents,
                {
                  name: attachment.name,
                  src: attachment.url,
                  type: TReportingDocumentType.other,
                },
              ]);
            }}
          />
          <div style={{ display: 'flex', paddingTop: 12, flexDirection: 'column', gap: 12 }}>
            {formik.values.documents.map((document, index) => (
              <div key={index} style={{ display: 'flex', gap: 12 }}>
                <div style={{ backgroundColor: Colors.E5GREY, borderRadius: 6, paddingLeft: 6 }}>
                  <Typography variant="caption">{document.name}</Typography>
                  <IconButton size="small" onClick={() => deleteFileClicked(index)}>
                    <Close />
                  </IconButton>
                </div>
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <Select {...formik.getFieldProps(`documents[${index}].type`)}>
                    <MenuItem value={TReportingDocumentType.energyCertificate}>
                      <NomenclatureLabel fieldName="documents.energyCertificate" defaultValue={t('general.recordtype.energyCertificate')} />
                    </MenuItem>
                    <MenuItem value={TReportingDocumentType.expertProtocol}>
                      <NomenclatureLabel fieldName="documents.expertProtocol" defaultValue={t('general.recordtype.expertProtocol')} />
                    </MenuItem>
                    <MenuItem value={TReportingDocumentType.landRegister}>
                      <NomenclatureLabel fieldName="documents.landRegister" defaultValue={t('general.recordtype.landRegister')} />
                    </MenuItem>
                    <MenuItem value={TReportingDocumentType.other}>{t('general.recordtype.other')}</MenuItem>
                    <MenuItem value={TReportingDocumentType.rentalAgreement}>
                      <NomenclatureLabel fieldName="documents.rentalAgreement" defaultValue={t('general.recordtype.rentalAgreement')} />
                    </MenuItem>
                    <MenuItem value={TReportingDocumentType.riskReport}>
                      <NomenclatureLabel fieldName="documents.riskReport" defaultValue={t('general.recordtype.riskReport')} />
                    </MenuItem>
                    <MenuItem value={TReportingDocumentType.utilityReport}>
                      <NomenclatureLabel fieldName="documents.utilityReport" defaultValue={t('general.recordtype.utilityReport')} />
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" color="inherit" onClick={handleCloseAndReset}>
            {t('dashboard.assets.records.addrecords.cancel')}
          </Button>
          <LoadingButton loading={isLoading} type="submit" variant="contained" disableElevation color="primary">
            {record ? t('dashboard.assets.records.addrecords.updatebutton') : t('dashboard.assets.records.addrecords.addbutton')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
